import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash } from 'react-icons/fa';
import { NS } from '../../U3129';
import { U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from '../../tools/u3129ReadonlyTableCellClass';
import {
  U3129_GRID_COMPACT_ROW_HOUR_TD_BASE,
  U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS,
  U3129_HONORARIUM_EXPANDED_SUBPROJECT_ROW_CLASS,
  U3129_HONORARIUM_GUTTER_TD_CLASS,
  U3129_HONORARIUM_META_COLUMN_CELL_X,
  U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS,
} from '../../tools/u3129TimesheetGridCompactStyles';
import type { U2000TimesheetWeekDay } from '../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import {
  U3129_HONORARIUM_NO_SUBPROJECT_KEY,
  type U3129HonorariumSubprojectGroup,
  u3129HonorariumSubprojectHeaderLabel,
} from '../../tools/u3129GroupHonorariumForDisplay';

export interface U3129HonorariumSubprojectHeaderRowProps {
  group: U3129HonorariumSubprojectGroup;
  visibleDays: readonly U2000TimesheetWeekDay[];
  showSubProjectColumn: boolean;
  metaColSpan: number;
  hourColClass: string;
  readonly: boolean;
  formatDayHours: (value: number) => string;
  formatTotalHours: (total: number) => string;
  onDeleteSubproject: () => void;
}

const U3129HonorariumSubprojectHeaderRow: React.FC<U3129HonorariumSubprojectHeaderRowProps> = ({
  group,
  visibleDays,
  showSubProjectColumn,
  metaColSpan,
  hourColClass,
  readonly,
  formatDayHours,
  formatTotalHours,
  onDeleteSubproject,
}) => {
  const { t } = useTranslation();
  const label = u3129HonorariumSubprojectHeaderLabel(group);
  const canDelete =
    !readonly &&
    group.subprojectKey !== U3129_HONORARIUM_NO_SUBPROJECT_KEY &&
    group.lines.length > 0;

  return (
    <tr className={U3129_HONORARIUM_EXPANDED_SUBPROJECT_ROW_CLASS}>
      <td className={`${U3129_HONORARIUM_GUTTER_TD_CLASS} ${U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS}`} />
      {showSubProjectColumn ? (
        <>
          <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1 align-middle`} />
          <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1 align-middle`} />
          <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1 align-middle`} />
          <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1 align-middle text-left text-sm font-medium text-gray-700`}>
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate">{label}</span>
              {canDelete ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  title={t('btnDeleteHonorariumSubproject', { ns: NS, defaultValue: 'Supprimer le sous-projet' })}
                  onClick={onDeleteSubproject}
                >
                  <FaTrash className="h-3 w-3" aria-hidden />
                  {t('btnDeleteHonorariumSubproject', { ns: NS, defaultValue: 'Supprimer le sous-projet' })}
                </button>
              ) : null}
            </div>
          </td>
        </>
      ) : (
        <td colSpan={metaColSpan} className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1 align-middle text-left text-sm text-gray-500`}>
          {t('lblHonorariumSubprojectTotals', { ns: NS, defaultValue: 'Totaux sous-projet' })}
        </td>
      )}
      <td className={`${U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS} p-0 border-0 py-1 align-middle`} aria-hidden="true" />
      {visibleDays.map((d) => (
        <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${hourColClass} align-middle text-right`}>
          <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums text-gray-700`}>
            {formatDayHours(group.totals.daySums[d] ?? 0)}
          </div>
        </td>
      ))}
      <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${hourColClass} align-middle text-right`}>
        <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums font-medium text-gray-800`}>
          {formatTotalHours(group.totals.grandTotal)}
        </div>
      </td>
    </tr>
  );
};

export default U3129HonorariumSubprojectHeaderRow;
